(() => {
  class CanvasCalculator {
    constructor(root) {
      this.root = root;
      this.q = selector => root.querySelector(selector);
      this.qa = selector => [...root.querySelectorAll(selector)];
      this.priceOptions = this.normalisePriceOptions(this.readJson('[data-price-options]'));
      this.frameOptions = this.normaliseFrameOptions(this.readJson('[data-frame-options]'));
      this.optionPositions = this.readJson('[data-option-positions]');
      this.variantCache = new Map();
      this.resolutionToken = 0;
      this.previousUnit = 'in';
      this.unlockedStep = 1;
      this.currentStep = 1;
      this.stepValidity = {};
      this.previewView = 'rear';
      this.previewAnimating = false;
      this.square = undefined;
      this.bind();
      this.updateLayoutOffsets();
      this.setupFrameViewer();
      this.renderStringingOptions();
      this.update();
      document.fonts?.ready?.then(() => this.stabiliseDescriptionAreas());
    }

    readJson(selector) {
      try {
        return JSON.parse(this.q(selector)?.textContent || '{}');
      } catch (error) {
        console.error(`Canvas Calculator: could not parse ${selector}.`, error);
        return {};
      }
    }

    normalisePriceOptions(options) {
      return Object.fromEntries(Object.entries(options).map(([label, id]) => {
        const value = Math.round(Number(String(label).replace(/[^0-9.]/g, '')));
        return [String(value), id];
      }).filter(([value]) => value !== 'NaN'));
    }

    normaliseFrameOptions(options) {
      return Object.fromEntries(Object.entries(options).map(([label, id]) => [label.trim().toLowerCase(), id]));
    }

    bind() {
      this.qa('input[name^="unit-"], input[name^="surface-"], input[name^="production-"], input[name^="frame-"], input[name^="delivery-"], input[name^="drings-"]')
        .forEach(input => input.addEventListener('change', event => {
          if (event.target.name.startsWith('unit-')) this.convertDimensions(event.target.value);
          this.update();
        }));

      ['[data-width]', '[data-height]', '[data-qty]', '[data-notes]', '[data-local-postcode]']
        .forEach(selector => this.q(selector)?.addEventListener('input', () => this.update()));
      ['[data-width]', '[data-height]'].forEach(selector => {
        this.q(selector).addEventListener('change', () => this.normaliseDimensionInputs());
        this.q(selector).addEventListener('blur', () => this.normaliseDimensionInputs());
      });

      this.qa('input[name^="artbox-"]').forEach(input => input.addEventListener('change', () => this.update()));

      let activeInfoButton = null;
      let activeInfoPanel = null;
      const closeInfo = () => {
        if (activeInfoPanel) this.setHidden(activeInfoPanel, true);
        activeInfoButton?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('cc6-modal-open');
        activeInfoPanel = null;
      };
      this.qa('[data-info-toggle]').forEach(infoButton => infoButton.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        closeInfo();
        const infoPanel = document.getElementById(infoButton.getAttribute('aria-controls'));
        if (!infoPanel) return;
        activeInfoButton = infoButton;
        activeInfoPanel = infoPanel;
        this.setHidden(infoPanel, false);
        infoButton.setAttribute('aria-expanded', 'true');
        document.body.classList.add('cc6-modal-open');
        infoPanel.querySelector('.cc6__info-close')?.focus();
      }));
      this.qa('[data-info-close]').forEach(button => button.addEventListener('click', event => {
        event.preventDefault();
        closeInfo();
        activeInfoButton?.focus();
      }));
      this.root.addEventListener('keydown', event => {
        if (event.key === 'Escape' && activeInfoPanel) {
          closeInfo();
          activeInfoButton?.focus();
        }
      });

      this.q('[data-minus]').addEventListener('click', () => this.changeQuantity(-1));
      this.q('[data-plus]').addEventListener('click', () => this.changeQuantity(1));
      this.qa('[data-preview-view]').forEach(button => button.addEventListener('click', () => {
        this.switchPreviewView(button.dataset.previewView);
      }));
      this.q('[data-product-form]').addEventListener('submit', event => this.submitOrder(event));
      this.q('[data-continue-shopping]').addEventListener('click', () => this.continueShopping());
      this.q('[data-wizard-next]').addEventListener('click', () => this.advanceWizard());
      this.q('[data-wizard-back]').addEventListener('click', () => this.retreatWizard());
      this.q('[data-wizard-cancel]').addEventListener('click', () => this.continueShopping());
      this.qa('[data-edit-step]').forEach(link => link.addEventListener('click', () => {
        this.editStep(Number(link.dataset.editStep));
      }));
      let resizeTimer;
      window.addEventListener('resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
          this.updateLayoutOffsets();
          this.stabiliseDescriptionAreas();
        }, 120);
      });

      // The header docks/undocks purely from scrolling (independent of any
      // wizard step change), so the sticky offset needs its own scroll
      // listener rather than relying solely on the resize/step-change hooks.
      let scrollTicking = false;
      window.addEventListener('scroll', () => {
        if (scrollTicking) return;
        scrollTicking = true;
        window.requestAnimationFrame(() => {
          this.updateStickyOffset();
          scrollTicking = false;
        });
      }, { passive: true });
    }

    checked(prefix) {
      return this.q(`input[name^="${prefix}"]:checked`)?.value;
    }

    numeric(selector) {
      return Number(this.q(selector)?.value);
    }

    datasetNumber(attribute, fallback) {
      const raw = this.root.getAttribute(attribute);
      const value = Number(raw);
      return raw !== null && raw !== '' && Number.isFinite(value) ? value : fallback;
    }

    convertDimensions(nextUnit) {
      if (!this.previousUnit) {
        this.previousUnit = nextUnit;
        return;
      }
      if (nextUnit === this.previousUnit) return;
      const factor = nextUnit === 'cm' ? 2.54 : 1 / 2.54;
      ['[data-width]', '[data-height]'].forEach(selector => {
        const field = this.q(selector);
        const value = Number(field.value);
        if (field.value !== '' && Number.isFinite(value)) {
          field.value = nextUnit === 'cm'
            ? this.formatCentimetres(value * factor)
            : String(Math.round(value * factor));
        }
      });
      this.previousUnit = nextUnit;
    }

    normaliseDimensionInputs() {
      const unit = this.checked('unit-') || 'in';
      ['[data-width]', '[data-height]'].forEach(selector => {
        const field = this.q(selector);
        if (!field || field.value.trim() === '') return;
        const value = Number(field.value);
        if (!Number.isFinite(value)) return;
        field.value = unit === 'cm'
          ? this.formatCentimetres(value)
          : String(Math.round(value));
      });
      this.update();
    }

    changeQuantity(delta) {
      const field = this.q('[data-qty]');
      const current = parseInt(field.value, 10) || 1;
      field.value = Math.max(1, Math.min(99, current + delta));
      this.update();
    }

    advanceWizard() {
      const current = this.currentStep;
      if (this.unlockedStep >= 9 && current < 9) {
        // Once Review Order has been reached, editing an earlier step's
        // Next button becomes a single "Review Order" button that returns
        // straight there - regardless of whether this step's own fields
        // are currently valid, since any resulting error is surfaced on
        // the Review Order screen itself rather than blocking navigation.
        this.editStep(9);
        return;
      }
      const pathValid = Array.from({ length: current }, (_, index) => index + 1)
        .every(step => this.stepValidity[step]);
      if (current >= 9 || !pathValid) return;
      const nextStepNumber = this.qa('[data-step]')
        .map(step => Number(step.dataset.step))
        .filter(step => step > current)
        .sort((a, b) => a - b)[0];
      if (!nextStepNumber) return;
      this.currentStep = nextStepNumber;
      if (nextStepNumber > this.unlockedStep) this.unlockedStep = nextStepNumber;
      this.update();
      this.goToStep(nextStepNumber);
    }

    retreatWizard() {
      if (this.currentStep <= 1) return;
      const previousStepNumber = this.qa('[data-step]')
        .map(step => Number(step.dataset.step))
        .filter(step => step < this.currentStep)
        .sort((a, b) => b - a)[0];
      // Back never truncates unlockedStep - the Order Summary keeps
      // showing everything already confirmed, same as an Edit link.
      this.editStep(previousStepNumber);
    }

    editStep(stepNumber) {
      if (!stepNumber || stepNumber < 1 || stepNumber > this.unlockedStep) return;
      this.currentStep = stepNumber;
      this.update();
      this.goToStep(stepNumber);
    }

    goToStep(stepNumber) {
      const step = this.q(`[data-step="${stepNumber}"]`);
      // CSS position:sticky can't keep the summary+step panel pinned near
      // the header while scrolling without either giving up on merging it
      // into one bordered box (nothing follows a sticky element without
      // risking overlap) or a much larger custom scroll-driven
      // implementation (see canvas-calculator.css's notes on
      // .cc6__step-recap for what was tried and why). Re-aligning the
      // whole panel's own top edge just under the header on every
      // Next/Back press instead sidesteps that entirely: no continuous
      // sticky tracking, just a discrete scroll each time the step
      // changes, targeting .cc6__form itself (which starts with the
      // recap) rather than the individual fieldset.
      const panel = this.q('.cc6__panel.cc6__form');
      // Let newly revealed/collapsed content finish measuring before
      // calculating the destination. This prevents layout changes during
      // the scroll.
      window.setTimeout(() => {
        if (panel) {
          this.updateLayoutOffsets();
          const offset = this.stickyHeaderOffset();
          const top = window.scrollY + panel.getBoundingClientRect().top - offset - 16;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
        step?.querySelector('input, textarea, button')?.focus({ preventScroll: true });
      }, 180);
    }

    stickyHeaderOffset() {
      // Measure whatever is currently pinned across the top of the viewport.
      // Expanse pins the whole header stack (main row + category sub-nav)
      // via a positioned ANCESTOR wrapper (a `.shopify-section.header-section`
      // with position:sticky) - the inner rows themselves compute
      // position:relative/absolute, so checking rows individually (or
      // matching only "site-header"/"header-wrapper" class fragments, as an
      // earlier version of this function did) misses that wrapper entirely
      // and under-reports the real offset once the page is scrolled past
      // the announcement bar. Match broadly on "header", then confirm with
      // computed position + geometry so unrelated content (including this
      // section's own .cc6__header) can't be mistaken for pinned chrome.
      let bottom = 0;
      const selectors = 'header, [class*="header"], [class*="announcement"]';
      document.querySelectorAll(selectors).forEach(element => {
        if (this.root.contains(element)) return;
        const style = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();
        if ((style.position === 'fixed' || style.position === 'sticky') && rect.top <= 10 && rect.bottom > 0) {
          bottom = Math.max(bottom, rect.bottom);
        }
      });
      return bottom || (window.innerWidth <= 760 ? 90 : 110);
    }

    updateStickyOffset() {
      // The pinned header's real height changes purely from scrolling (the
      // announcement bar drops out once scrolled past), independent of the
      // wizard steps, so the sticky summary's offset has to track scroll
      // position too - not just resize/step-change - or it silently goes
      // stale the moment the page scrolls. This only updates the CSS
      // variable; it deliberately does not force a sticky recalc (see
      // nudgeStickyRecalc) since this also runs on every scroll frame and a
      // forced scroll-based nudge here would re-trigger this same scroll
      // listener.
      this.root.style.setProperty('--cc6-sticky-top', `${this.stickyHeaderOffset()}px`);
    }

    nudgeStickyRecalc() {
      // Chromium has a known bug where a position:sticky element's "stuck"
      // box goes stale after a sibling's size changes (exactly what happens
      // when the wizard reveals the next fieldset and the left column
      // grows) and stops tracking its offset - confirmed live: computed
      // style correctly reported position:sticky/top:127px, but the
      // rendered box sat at a stale ~10px instead. That split is the
      // giveaway that main-thread layout and the compositor's cached
      // scroll-to-sticky mapping had gone out of sync: forcing a
      // display:none/restore reflow only fixes the former. The documented
      // fix for the latter is an actual scroll delta, which is what forces
      // the compositor to rebuild the mapping from current geometry.
      const summary = this.q('.cc6__summary');
      if (summary) {
        const previousDisplay = summary.style.display;
        summary.style.display = 'none';
        void summary.offsetHeight;
        summary.style.display = previousDisplay;
      }
      const y = window.scrollY;
      window.scrollTo(window.scrollX, y + 1);
      window.scrollTo(window.scrollX, y);
    }

    updateLayoutOffsets() {
      const summary = this.q('.cc6__summary');
      const preview = this.q('.cc6__preview');
      const grid = this.q('.cc6__grid');

      // Clear positioning left behind by earlier V6 experiments. The complete
      // desktop summary is now controlled by one stable CSS sticky rule, so
      // scrolling never recalculates or rewrites its coordinates.
      if (summary) {
        [
          'position', 'top', 'left', 'right', 'bottom', 'width', 'height',
          'max-height', 'overflow', 'align-self'
        ].forEach(property => summary.style.removeProperty(property));
      }
      preview?.style.removeProperty('--cc6-sticky-svg-height');
      grid?.style.removeProperty('min-height');
      this.updateStickyOffset();
      this.nudgeStickyRecalc();
    }

    renderStringingOptions() {
      const width = this.numeric('[data-width]');
      const height = this.numeric('[data-height]');
      const isSquare = Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0 && Math.abs(width - height) < 0.001;
      const box = this.q('[data-string-options]');
      const current = this.checked('string-');
      // Grid order: squares show Yes on the left. Rectangles show Portrait
      // and Landscape down the left column, with Both and No String at right.
      const options = isSquare
        ? [['yes', 'Yes Please String'], ['no', 'No String']]
        : [['portrait', 'Portrait'], ['both', 'Both Landscape & Portrait'], ['landscape', 'Landscape'], ['no', 'No String']];
      const preferred = isSquare ? 'yes' : 'portrait';
      const selected = options.some(option => option[0] === current) ? current : preferred;

      box.innerHTML = options.map(([value, label]) => `
        <label class="cc6__choice">
          <input type="radio" name="string-${this.root.dataset.sectionId}" value="${value}" ${value === selected ? 'checked' : ''}>
          <span><strong>${label}</strong></span>
        </label>
      `).join('');

      this.q('[data-string-help]').textContent = isSquare
        ? 'Choose whether you would like the square canvas strung.'
        : 'Which way should we string the canvas?';

      box.querySelectorAll('input').forEach(input => input.addEventListener('change', () => this.update()));
    }

    stabiliseDescriptionAreas() {
      const regions = [
        {
          element: this.q('[data-delivery-description]'),
          texts: this.qa('input[name^="delivery-"]').map(input => input.dataset.description || '')
        },
        {
          element: this.q('[data-frame-description]'),
          texts: this.qa('input[name^="frame-"]').map(input => input.dataset.description || '')
        },
        {
          element: this.q('[data-artbox-description]'),
          texts: [
            'Boxing is mandatory when shipping the canvas. This option has been automatically selected to protect the canvas during transportation. Each canvas will be delivered in its own box.',
            'Add a box to protect your canvas. Each canvas will be packaged in its own box and can be reused for shipping once the canvas has been painted.'
          ]
        }
      ];

      regions.forEach(({ element }) => { if (element) element.style.minHeight = ''; });
      if (window.innerWidth <= 760) return;

      window.requestAnimationFrame(() => regions.forEach(({ element, texts }) => {
        if (!element || element.getBoundingClientRect().width < 20) return;
        const parent = element.parentElement;
        const clone = element.cloneNode(false);
        clone.hidden = false;
        clone.removeAttribute('id');
        Object.assign(clone.style, {
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          width: `${element.getBoundingClientRect().width}px`,
          minHeight: '0',
          height: 'auto',
          inset: 'auto'
        });
        parent.appendChild(clone);
        let maximum = 0;
        texts.filter(Boolean).forEach(text => {
          clone.textContent = text;
          maximum = Math.max(maximum, clone.getBoundingClientRect().height);
        });
        clone.remove();
        if (maximum) element.style.minHeight = `${Math.ceil(maximum)}px`;
      }));
    }

    getSurfacePrice(surface) {
      const map = {
        '12': this.datasetNumber('data-price-12', 210),
        '10': this.datasetNumber('data-price-10', 190),
        'poly': this.datasetNumber('data-price-poly', 170),
        'linen-white': this.datasetNumber('data-price-linen-white', 280),
        'linen-clear': this.datasetNumber('data-price-linen-clear', 300)
      };
      return map[surface] ?? 0;
    }

    surfaceLabel(surface) {
      return {
        '12': '12oz Cotton',
        '10': '10oz Cotton',
        'poly': 'Polycotton',
        'linen-white': 'Linen (white primed)',
        'linen-clear': 'Linen (transparent primed)'
      }[surface] || surface;
    }

    stringingLabel(value) {
      return {
        no: 'No String',
        yes: 'Yes Please String',
        landscape: 'Landscape',
        portrait: 'Portrait',
        both: 'Both Landscape & Portrait'
      }[value] || 'No String';
    }

    frameLabel(value) {
      return {
        none: 'No Float Frame',
        oak: 'Natural Timber Frame',
        black: 'Black Frame with Wood Grain',
        white: 'White Frame with Wood Grain'
      }[value] || 'No Float Frame';
    }

    deliveryLabel(value) {
      return {
        shipped: 'Shipped via Courier Service',
        pickup: 'Pick Up In-Store',
        local: 'Local Delivery (Free)'
      }[value] || 'Pick Up In-Store';
    }

    setupFrameViewer() {
      const images = this.qa('.cc6__frame-image img');
      if (!images.length) return;
      const viewer = document.createElement('div');
      viewer.className = 'cc6__image-viewer';
      viewer.hidden = true;
      viewer.style.cssText = 'position:fixed!important;inset:0!important;z-index:2147483000!important;display:none;place-items:center;padding:28px;background:#fff;box-sizing:border-box;';
      viewer.innerHTML = `<button type="button" class="cc6__image-viewer-backdrop" aria-label="Close enlarged frame image"></button><div class="cc6__image-viewer-stage" role="dialog" aria-modal="true" aria-label="Enlarged float-frame image"><img alt=""><div class="cc6__image-viewer-controls"><button type="button" data-viewer-prev aria-label="Previous frame image">&#8249;</button><button type="button" data-viewer-close aria-label="Close enlarged frame image">&times;</button><button type="button" data-viewer-next aria-label="Next frame image">&#8250;</button></div></div>`;
      document.body.appendChild(viewer);
      const backdrop = viewer.querySelector('.cc6__image-viewer-backdrop');
      const stage = viewer.querySelector('.cc6__image-viewer-stage');
      const controls = viewer.querySelector('.cc6__image-viewer-controls');
      backdrop.style.cssText = 'position:absolute!important;inset:0!important;width:100%!important;height:100%!important;padding:0!important;border:0!important;background:#fff!important;cursor:zoom-out;';
      stage.style.cssText = 'position:relative!important;z-index:1!important;display:grid!important;place-items:center!important;width:min(1650px,98vw)!important;height:min(96vh,1350px)!important;pointer-events:none!important;';
      controls.style.cssText = 'position:absolute!important;bottom:0!important;left:50%!important;display:flex!important;align-items:center!important;gap:10px!important;transform:translateX(-50%)!important;pointer-events:auto!important;';
      controls.querySelectorAll('button').forEach(button => {
        button.style.cssText = 'display:grid!important;place-items:center!important;width:48px!important;height:48px!important;min-width:48px!important;padding:0!important;border:1px solid #ddd!important;border-radius:50%!important;background:#fff!important;color:#111!important;box-shadow:0 3px 12px rgba(0,0,0,.14)!important;font-size:1.8rem!important;line-height:1!important;cursor:pointer!important;';
      });
      const closeButton = controls.querySelector('[data-viewer-close]');
      closeButton.style.width = '58px';
      closeButton.style.height = '58px';
      closeButton.style.minWidth = '58px';
      let index = 0;
      const largeImage = viewer.querySelector('img');
      largeImage.style.cssText = 'display:block!important;width:min(760px,calc(100vw - 56px))!important;min-width:min(760px,calc(100vw - 56px))!important;height:auto!important;max-width:calc(100vw - 56px)!important;max-height:calc(100vh - 112px)!important;object-fit:contain!important;box-shadow:none!important;';
      const render = () => {
        const source = images[index];
        const sourceUrl = source.currentSrc || source.src;
        largeImage.src = sourceUrl
          .replace(/([?&])width=\d+/i, '$1width=1600')
          .replace(/_480x300(?=\.[a-z0-9]+(?:\?|$))/i, '');
        largeImage.alt = source.alt || 'Float-frame option';
      };
      const close = () => {
        viewer.hidden = true;
        viewer.style.display = 'none';
        document.body.classList.remove('cc6-modal-open');
      };
      const open = nextIndex => {
        index = nextIndex;
        render();
        viewer.hidden = false;
        viewer.style.display = 'grid';
        document.body.classList.add('cc6-modal-open');
        viewer.querySelector('[data-viewer-close]').focus();
      };
      images.forEach((image, imageIndex) => image.addEventListener('click', event => {
        const card = image.closest('.cc6__frame-card');
        const input = card?.querySelector('input[type="radio"]');
        if (!input?.checked) return;
        event.preventDefault();
        event.stopPropagation();
        open(imageIndex);
      }));
      viewer.querySelector('.cc6__image-viewer-backdrop').addEventListener('click', close);
      viewer.querySelector('[data-viewer-close]').addEventListener('click', close);
      viewer.querySelector('[data-viewer-prev]').addEventListener('click', () => { index = (index - 1 + images.length) % images.length; render(); });
      viewer.querySelector('[data-viewer-next]').addEventListener('click', () => { index = (index + 1) % images.length; render(); });
      document.addEventListener('keydown', event => {
        if (viewer.hidden) return;
        if (event.key === 'Escape') close();
        if (event.key === 'ArrowLeft') viewer.querySelector('[data-viewer-prev]').click();
        if (event.key === 'ArrowRight') viewer.querySelector('[data-viewer-next]').click();
      });
    }

    getFrameRate(frame) {
      return {
        none: 0,
        oak: this.datasetNumber('data-frame-oak', 55),
        black: this.datasetNumber('data-frame-black', 55),
        white: this.datasetNumber('data-frame-white', 55)
      }[frame] ?? 0;
    }

    updateFrameImages(surface) {
      const useLinen = surface === 'linen-clear';
      this.qa('.cc6__frame-card img').forEach(image => {
        const nextSource = useLinen ? image.dataset.linenSrc : image.dataset.whiteSrc;
        if (nextSource && image.src !== nextSource) image.src = nextSource;
      });
    }

    update() {
      const selectedUnit = this.checked('unit-');
      const selectedSurface = this.checked('surface-');
      const selectedProduction = this.checked('production-');
      const selectedFrame = this.checked('frame-');
      const selectedDelivery = this.checked('delivery-');
      const selectedDRings = this.checked('drings-');
      const selectedStringing = this.checked('string-');
      const unit = selectedUnit || 'in';
      const widthField = this.q('[data-width]');
      const heightField = this.q('[data-height]');
      const width = widthField.value.trim() === '' ? NaN : Number(widthField.value);
      const height = heightField.value.trim() === '' ? NaN : Number(heightField.value);
      const isSquare = Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0 && Math.abs(width - height) < 0.001;

      if (this.square !== undefined && this.square !== isSquare) this.renderStringingOptions();
      this.square = isSquare;

      const surface = selectedSurface || '12';
      const production = selectedProduction || 'standard';
      const frame = selectedFrame || 'none';
      const delivery = selectedDelivery || 'pickup';
      const dRings = selectedDRings === 'yes';
      const quantity = Math.max(1, Math.min(99, parseInt(this.q('[data-qty]').value, 10) || 1));
      const stringing = selectedStringing || 'no';
      const widthMetres = unit === 'cm' ? width / 100 : width * 0.0254;
      const heightMetres = unit === 'cm' ? height / 100 : height * 0.0254;
      const area = widthMetres * heightMetres;
      const artboxInput = this.q('[data-artbox]');
      const artboxNoInput = this.q('[data-artbox-no]');
      const artboxNoLabel = this.q('[data-artbox-no-label]');
      const boxLimit = unit === 'cm' ? 91 : 36;
      const tooLargeForArtBox = Number.isFinite(width) && Number.isFinite(height) && (width > boxLimit || height > boxLimit);
      const forcedArtBox = tooLargeForArtBox ? 'no' : selectedDelivery === 'shipped' ? 'yes' : null;
      if (artboxNoLabel) artboxNoLabel.textContent = tooLargeForArtBox ? 'Box Unavailable' : 'No Box Required';
      if (forcedArtBox) {
        artboxInput.checked = forcedArtBox === 'yes';
        artboxNoInput.checked = forcedArtBox === 'no';
        artboxInput.disabled = forcedArtBox === 'no';
        artboxNoInput.disabled = forcedArtBox === 'yes';
      } else {
        if (this.forcedArtBox) {
          artboxInput.checked = false;
          artboxNoInput.checked = true;
        }
        artboxInput.disabled = false;
        artboxNoInput.disabled = false;
      }
      this.forcedArtBox = forcedArtBox;
      const artbox = artboxInput.checked;
      const calculationReady = Number.isFinite(area) && area > 0;
      const canvasBasePrice = calculationReady ? this.datasetNumber('data-canvas-base-price', 35) : 0;
      const canvasPrice = calculationReady ? area * this.getSurfacePrice(surface) : 0;
      const framePrice = calculationReady ? 2 * (widthMetres + heightMetres) * this.getFrameRate(frame) : 0;
      const artboxRate = this.datasetNumber('data-artbox-rate', 20);
      const artboxBase = this.datasetNumber('data-artbox-base', 5);
      const artboxAmount = artbox && calculationReady ? artboxBase + area * artboxRate : 0;
      // D-rings are charged as one pair whenever selected. When the canvas is
      // strung both ways, the preview may show both orientations, but the
      // customer is still charged for only one pair.
      const dRingPairs = dRings ? 1 : 0;
      const dRingAmount = dRingPairs * this.datasetNumber('data-dring-pair', 2);
      const beforeExpress = canvasBasePrice + canvasPrice + framePrice + artboxAmount + dRingAmount;
      const expressPercent = this.datasetNumber('data-express', 10);
      const expressAmount = production === 'express' ? beforeExpress * expressPercent / 100 : 0;
      const rawUnitPrice = beforeExpress + expressAmount;
      const unitPriceExShipping = calculationReady ? Math.max(2, Math.floor(rawUnitPrice / 2) * 2) : 0;
      const roundingDeductionPerUnit = Math.max(0, rawUnitPrice - unitPriceExShipping);
      // Rounded to its own nearest $2 rather than folded into the same
      // rounding step as the rest of the price - otherwise toggling
      // Shipped on/off shifts the OTHER rounding remainder too (since it's
      // computed against a different raw total), making the Canvas figure
      // above appear to change with the delivery method even though it
      // hasn't. Kept out of the express-surcharge calculation as well -
      // express is a faster-PRODUCTION surcharge, it shouldn't inflate
      // shipping. Both pieces are already even, so their sum is too, which
      // is what unitPrice needs to keep matching a $2-step Price variant.
      const shippingBase = this.datasetNumber('data-shipping-base', 0);
      const shippingRate = this.datasetNumber('data-shipping-rate', 0);
      const rawShippingAmount = calculationReady && delivery === 'shipped' ? shippingBase + area * shippingRate : 0;
      const shippingAmount = Math.round(rawShippingAmount / 2) * 2;
      const unitPrice = unitPriceExShipping + shippingAmount;
      const min = unit === 'cm' ? 30 : this.datasetNumber('data-min', 12);
      const maxPrice = this.datasetNumber('data-max-price', 1000);
      const displayUnit = unit === 'in' ? 'inches' : 'cm';
      const dimensionsPresent = Number.isFinite(width) && Number.isFinite(height);
      const size = dimensionsPresent ? `${this.trim(width)} × ${this.trim(height)} ${displayUnit}` : '—';
      const surfaceText = selectedSurface ? this.surfaceLabel(surface) : '—';
      const stringingText = selectedStringing ? this.stringingLabel(stringing) : '—';
      const productionText = selectedProduction ? (production === 'express' ? 'Express (2 business days)' : 'Standard (approximately 2 weeks)') : '—';
      const frameText = selectedFrame ? this.frameLabel(frame) : '—';
      const artboxText = this.checked('artbox-') ? (artbox ? 'Included' : 'Not included') : '—';
      const deliveryText = selectedDelivery ? this.deliveryLabel(delivery) : '—';
      const dRingsText = selectedDRings ? (dRings ? `${dRingPairs} pair${dRingPairs === 1 ? '' : 's'}` : 'No') : '—';
      const selectedFrameInput = this.q('input[name^="frame-"]:checked');
      const frameDescription = selectedFrameInput?.dataset.description?.trim() || '';
      const selectedDeliveryInput = this.q('input[name^="delivery-"]:checked');
      // Existing section instances may retain an older saved description.
      // Normalise those references so the displayed radius is consistently 10km.
      const deliveryDescription = (selectedDeliveryInput?.dataset.description?.trim() || '')
        .replace(/\b5\s*km\b/gi, '10km');
      const postcodeField = this.q('[data-local-postcode]');
      const postcode = (postcodeField?.value || '').replace(/\D/g, '').slice(0, 4);
      if (postcodeField && postcodeField.value !== postcode) postcodeField.value = postcode;
      const localPostcodes = new Set((this.root.dataset.localPostcodes || '')
        .split(/[,\s]+/).map(value => value.trim()).filter(Boolean));
      const postcodeSuburbs = {
        '2061': 'Kirribilli / Milsons Point',
        '2062': 'Cammeray',
        '2063': 'Northbridge',
        '2064': 'Artarmon',
        '2065': 'Crows Nest / Greenwich / St Leonards / Wollstonecraft',
        '2066': 'Lane Cove area',
        '2067': 'Chatswood',
        '2068': 'Willoughby / Castlecrag / Middle Cove',
        '2069': 'Roseville',
        '2070': 'Lindfield',
        '2085': 'Belrose / Davidson',
        '2086': 'Frenchs Forest',
        '2087': 'Forestville / Killarney Heights',
        '2088': 'Mosman',
        '2089': 'Neutral Bay / Kurraba Point',
        '2090': 'Cremorne / Cremorne Point',
        '2092': 'Seaforth',
        '2093': 'Balgowlah / Clontarf / Manly Vale / North Balgowlah',
        '2094': 'Fairlight',
        '2095': 'Manly',
        '2096': 'Curl Curl / Freshwater / Queenscliff',
        '2097': 'Collaroy / Wheeler Heights',
        '2099': 'Cromer / Dee Why / Narraweena / North Curl Curl',
        '2100': 'Allambie Heights / Brookvale / North Manly / Oxford Falls'
      };
      const postcodeComplete = /^\d{4}$/.test(postcode);
      const postcodeEligible = postcodeComplete && localPostcodes.has(postcode);
      const dimensionErrors = this.validateDimensions({ width, height, min, unit, delivery });
      const dimensionsInvalid = Boolean(dimensionErrors.width || dimensionErrors.height);
      const displayedDimensionErrors = {
        width: widthField.value.trim() === '' ? '' : dimensionErrors.width,
        height: heightField.value.trim() === '' ? '' : dimensionErrors.height
      };
      const stepsComplete = {
        1: Boolean(selectedDelivery && (delivery !== 'local' || postcodeEligible)),
        2: Boolean(selectedDelivery && selectedUnit && !dimensionsInvalid),
        3: Boolean(selectedSurface),
        4: Boolean(selectedFrame),
        5: Boolean(selectedStringing && selectedDRings),
        6: Boolean(this.checked('artbox-')),
        7: Boolean(selectedProduction),
        8: quantity >= 1
      };
      const configurationComplete = [1, 2, 3, 4, 5, 6, 7, 8].every(step => stepsComplete[step]) && this.unlockedStep >= 8;
      this.updateStepVisibility(stepsComplete);
      let error = '';

      if (configurationComplete && !this.root.dataset.productUrl) {
        error = 'Select the Custom Made Canvas pricing product in the section settings.';
      } else if (configurationComplete && unitPrice > maxPrice) {
        error = `This configuration exceeds the $${maxPrice} online ordering limit. Please contact us for a quotation.`;
      } else if (configurationComplete && !this.priceOptions[String(unitPrice)]) {
        error = `The pricing product does not contain the $${unitPrice} Price option.`;
      } else if (configurationComplete && !this.frameOptions[frameText.toLowerCase()]) {
        error = `The pricing product does not contain the "${frameText}" Float Frame option.`;
      }

      this.renderDimensionValidation(displayedDimensionErrors);

      this.qa('[data-input-unit]').forEach(element => { element.textContent = unit === 'in' ? 'in' : 'cm'; });
      [widthField, heightField].forEach(field => { field.step = unit === 'cm' ? '0.1' : '1'; });
      this.q('[data-dimension-help]').textContent = selectedUnit ? this.dimensionHelp({ delivery, min, unit }) : '';
      this.q('[data-count]').textContent = this.q('[data-notes]')?.value.length || 0;
      this.q('[data-recap-delivery]').textContent = deliveryText;
      this.q('[data-recap-size]').textContent = size;
      this.q('[data-recap-surface]').textContent = surfaceText;
      this.q('[data-recap-frame]').textContent = frameText;
      this.q('[data-recap-string]').textContent = stringingText;
      this.q('[data-recap-drings]').textContent = dRingsText;
      this.q('[data-recap-artbox]').textContent = artboxText;
      this.q('[data-recap-qty]').textContent = quantity;
      this.q('[data-recap-production]').textContent = productionText;
      this.q('[data-delivery-description]').textContent = deliveryDescription.replace(/\s*Max canvas size:/i, '\nMax canvas size:');
      this.setHidden(this.q('[data-postcode-check]'), delivery !== 'local');
      const postcodeCheck = this.q('[data-postcode-check]');
      const postcodeStatus = this.q('[data-postcode-status]');
      if (postcodeStatus) {
        postcodeStatus.textContent = !postcode
          ? 'Enter your postcode to continue.'
          : !postcodeComplete
            ? 'Enter a four-digit postcode.'
            : postcodeEligible
              ? `Local Delivery appears available for ${postcodeSuburbs[postcode] || `postcode ${postcode}`}.`
              : 'This postcode appears to be outside our 10km Local Delivery area. Please choose pickup or courier, or call ArtSmart if you believe it is eligible.';
        postcodeStatus.classList.toggle('is-valid', postcodeEligible);
        postcodeStatus.classList.toggle('is-invalid', Boolean(postcode) && !postcodeEligible);
      }
      postcodeCheck?.classList.toggle('is-valid', postcodeEligible);
      postcodeCheck?.classList.toggle('is-invalid', delivery === 'local' && Boolean(postcode) && (!postcodeComplete || !postcodeEligible));
      this.q('[data-frame-description]').textContent = frameDescription;
      this.setHidden(this.q('[data-frame-description]'), !frameDescription);
      this.q('[data-artbox-description]').textContent = tooLargeForArtBox
        ? `The selected canvas is too large for an ArtSmart Art Box. Boxes are only available when both canvas sides are ${unit === 'cm' ? '91 cm' : '36 inches'} or smaller.`
        : delivery === 'shipped'
          ? 'Boxing is mandatory when shipping the canvas. This option has been automatically selected to protect the canvas during transportation. Each canvas will be delivered in its own box.'
          : 'Add a box to protect your canvas. Each canvas will be packaged in its own box and can be reused for shipping once the canvas has been painted.';
      this.q('[data-artbox-note]').textContent = tooLargeForArtBox
        ? `Box unavailable: this canvas is too large for an ArtSmart Art Box. Both sides must be ${unit === 'cm' ? '91 cm' : '36 inches'} or smaller.`
        : '';
      const noHangingOptions = stringing === 'no' && !dRings;
      this.setHidden(this.q('[data-hanging-warning]'), !noHangingOptions);
      // D-Ring hardware is included within the canvas figure rather than
      // exposed as a separate customer-facing charge.
      // The configurable base charge is included within the Canvas figure and
      // is intentionally not exposed as a separate customer-facing line item.
      // Absorb the hidden rounding deduction into the displayed Canvas figure
      // so the optional breakdown still reconciles to the Shopify price.
      const displayedCanvasPrice = Math.max(0, canvasBasePrice + canvasPrice + dRingAmount - roundingDeductionPerUnit);
      this.q('[data-canvas-price]').textContent = this.money(displayedCanvasPrice);
      this.q('[data-frame-price]').textContent = this.money(framePrice);
      this.q('[data-artbox-price]').textContent = this.money(artboxAmount);
      this.q('[data-unit-price]').textContent = `${this.money(unitPrice)} AUD`;
      const fullTotal = unitPrice * quantity;
      const volumeDiscountPercent = quantity >= 16 ? 15 : quantity >= 8 ? 10 : quantity >= 4 ? 8 : quantity >= 2 ? 5 : 0;
      const estimatedVolumeDiscount = fullTotal * volumeDiscountPercent / 100;
      const estimatedDiscountedTotal = fullTotal - estimatedVolumeDiscount;
      this.q('[data-total]').textContent = `${this.money(fullTotal)} AUD`;
      this.q('[data-volume-discount-percent]').textContent = `${volumeDiscountPercent}%`;
      this.q('[data-volume-discount-amount]').textContent = `−${this.money(estimatedVolumeDiscount)}`;
      this.q('[data-discounted-total]').textContent = `${this.money(estimatedDiscountedTotal)} AUD`;
      this.setHidden(this.q('[data-volume-discount-row]'), volumeDiscountPercent === 0);
      this.setHidden(this.q('[data-discounted-total-row]'), volumeDiscountPercent === 0);
      this.setHidden(this.q('[data-frame-price-row]'), frame === 'none');
      this.setHidden(this.q('[data-artbox-price-row]'), !artbox);
      this.setHidden(this.q('[data-express-row]'), production !== 'express');
      this.q('[data-express-amount]').textContent = this.money(expressAmount);
      this.setHidden(this.q('[data-express-badge]'), production !== 'express');
      this.setHidden(this.q('[data-shipping-row]'), delivery !== 'shipped');
      this.q('[data-shipping-amount]').textContent = this.money(shippingAmount);
      // Reveal pricing in the Order Summary once the user reaches Quantity
      // (STEP 8).
      this.setHidden(this.q('[data-pricing]'), this.unlockedStep < 8);
      this.updateFrameImages(surface);

      // Rather than drawing the selected Float Frame onto the canvas
      // preview itself, show it as a separate "+ [frame swatch]" next to
      // the canvas - reusing the same swatch image already shown on its
      // STEP 4 card (kept in sync with the surface by updateFrameImages
      // just above), rather than maintaining a second copy of it.
      const frameIndicator = this.q('[data-frame-indicator]');
      if (frameIndicator) {
        const showFrameIndicator = this.unlockedStep >= 4 && frame !== 'none';
        this.setHidden(frameIndicator, !showFrameIndicator);
        if (showFrameIndicator) {
          const sourceImage = selectedFrameInput?.closest('.cc6__frame-card')?.querySelector('.cc6__frame-image img');
          const indicatorImage = this.q('[data-frame-indicator-img]');
          if (sourceImage && indicatorImage) {
            indicatorImage.src = sourceImage.currentSrc || sourceImage.src;
            indicatorImage.alt = sourceImage.alt;
          }
          this.q('[data-frame-indicator-label]').textContent = frameText;
        }
      }

      const gstRate = this.datasetNumber('data-gst', 10);
      // Shopify receives the undiscounted total. The storefront estimate above
      // is display-only so Shopify can evaluate discounts across the full cart.
      const total = fullTotal;
      const gst = gstRate > 0 ? total - total / (1 + gstRate / 100) : 0;
      this.q('[data-gst-line]').textContent = gstRate > 0 ? `Includes ${this.money(gst)} GST` : '';

      this.renderPreview(dimensionsPresent ? width : 12, dimensionsPresent ? height : 12, stringing, surface, dRings, dimensionsPresent);
      // Description heights are measured at startup and after a viewport
      // resize. Re-measuring them during every live price update briefly
      // collapses content above the active dimension fields, which causes
      // Chrome to move the page while the customer is typing.
      const order = { quantity, size, deliveryText, postcode: delivery === 'local' ? postcode : '', surfaceText, stringingText, dRingsText, productionText, frameText, artboxText, area, unitPrice, total, roundingDeductionPerUnit, volumeDiscountPercent, estimatedVolumeDiscount, estimatedDiscountedTotal };
      this.currentOrder = order;
      this.populateForm({ ...order, variant: null });

      this.q('[data-add]').disabled = true;
      const status = this.q('[data-status]');
      status.textContent = error;
      this.setHidden(status, !error);
      this.renderDebug({ unitPrice, variant: null, error: error || (configurationComplete ? 'Resolving Shopify combination…' : 'Complete the guided steps') });

      const token = ++this.resolutionToken;
      if (configurationComplete && !error) {
        this.resolveVariant(unitPrice, frameText).then(variant => {
          if (token !== this.resolutionToken) return;
          let variantError = '';
          if (!variant?.id) {
            variantError = `The ${frameText} combination at ${this.money(unitPrice)} could not be found in Shopify.`;
          } else if (Math.round(Number(variant.price)) !== unitPrice) {
            variantError = `The matched Shopify variant is not priced at ${this.money(unitPrice)}.`;
          } else if (variant.available === false) {
            variantError = 'This canvas and Float Frame combination is unavailable.';
          }

          status.textContent = variantError;
          this.setHidden(status, !variantError);
          this.q('[data-add]').disabled = Boolean(variantError);
          this.populateForm({ ...order, variant: variantError ? null : variant });
          this.renderDebug({ unitPrice, variant, error: variantError });
        }).catch(() => {
          if (token !== this.resolutionToken) return;
          const variantError = `The ${frameText} combination at ${this.money(unitPrice)} could not be found in Shopify.`;
          status.textContent = variantError;
          this.setHidden(status, false);
          this.q('[data-add]').disabled = true;
          this.renderDebug({ unitPrice, variant: null, error: variantError });
        });
      }
    }

    async submitOrder(event) {
      event.preventDefault();
      if (this.adding) return;
      const form = event.currentTarget;
      const button = this.q('[data-add]');
      const buttonText = button.querySelector('span');
      const status = this.q('[data-status]');
      if (!this.q('[data-variant-id]').value || !this.currentOrder) return;

      this.adding = true;
      button.disabled = true;
      if (buttonText) buttonText.textContent = 'Adding to Cart…';
      status.textContent = '';
      this.setHidden(status, true);

      try {
        const rootPath = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${rootPath}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.description || 'The canvas could not be added to the cart.');
        }
        await response.json();
        this.showOrderConfirmation();
      } catch (error) {
        status.textContent = error.message || 'The canvas could not be added to the cart. Please try again.';
        this.setHidden(status, false);
        button.disabled = false;
      } finally {
        this.adding = false;
        if (buttonText) buttonText.textContent = 'Add to Cart';
      }
    }

    showOrderConfirmation() {
      const order = this.currentOrder;
      this.q('[data-confirm-delivery]').textContent = order.deliveryText;
      this.q('[data-confirm-size]').textContent = order.size;
      this.q('[data-confirm-surface]').textContent = order.surfaceText;
      this.q('[data-confirm-stringing]').textContent = order.stringingText;
      this.q('[data-confirm-drings]').textContent = order.dRingsText;
      this.q('[data-confirm-production]').textContent = order.productionText;
      this.q('[data-confirm-frame]').textContent = order.frameText;
      this.q('[data-confirm-artbox]').textContent = order.artboxText;
      this.q('[data-confirm-quantity]').textContent = order.quantity;
      this.q('[data-confirm-total]').textContent = `${this.money(order.total)} AUD`;
      this.q('[data-confirm-discount]').textContent = order.volumeDiscountPercent > 0
        ? `−${this.money(order.estimatedVolumeDiscount)} (${order.volumeDiscountPercent}%)`
        : this.money(0);
      this.q('[data-confirm-final]').textContent = `${this.money(order.estimatedDiscountedTotal)} AUD`;
      const modal = this.q('[data-order-confirmation]');
      this.setHidden(modal, false);
      document.body.classList.add('cc6-modal-open');
      this.q('[data-proceed-checkout]')?.focus();
    }

    continueShopping() {
      const url = new URL(window.location.href);
      url.searchParams.set('_canvas_reset', Date.now().toString());
      window.location.assign(url.toString());
    }

    async resolveVariant(unitPrice, frameText) {
      const key = `${unitPrice}|${frameText}`;
      if (this.variantCache.has(key)) return this.variantCache.get(key);

      const priceId = this.priceOptions[String(unitPrice)];
      const frameId = this.frameOptions[frameText.toLowerCase()];
      if (!priceId || !frameId) return null;

      const optionIds = [
        { position: Number(this.optionPositions.price || 1), id: priceId },
        { position: Number(this.optionPositions.frame || 2), id: frameId }
      ].sort((a, b) => a.position - b.position).map(option => option.id);

      const separator = this.root.dataset.productUrl.includes('?') ? '&' : '?';
      const parameters = new URLSearchParams({
        view: 'canvas-variant-resolver',
        option_values: optionIds.join(',')
      });
      const pageParameters = new URLSearchParams(window.location.search);
      ['preview_key', 'preview_theme_id'].forEach(name => {
        const value = pageParameters.get(name);
        if (value) parameters.set(name, value);
      });
      parameters.set('_cc6', String(Date.now()));
      const url = `${this.root.dataset.productUrl}${separator}${parameters.toString()}`;
      const response = await fetch(url, {
        cache: 'no-store',
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error(`Variant resolver returned ${response.status}`);
      const variant = await response.json();
      this.variantCache.set(key, variant);
      return variant;
    }

    updateStepVisibility(complete) {
      this.stepValidity = complete;
      // True while editing a step reopened from the Review Order screen
      // (via an Edit link, or Back once Review Order has been reached).
      const reviewingStep = this.unlockedStep >= 9 && this.currentStep < 9;
      // CSS reads this to hide each step's "Step N" label while
      // reviewingStep - reopening a step from Review Order should feel
      // like a focused edit, not a return trip through the numbered flow.
      this.root.classList.toggle('cc6--reviewing-step', reviewingStep);
      // The intro heading/description only makes sense before the wizard
      // has started - once past STEP 1 it's just dead space above the
      // active step.
      this.setHidden(this.q('.cc6__header'), this.currentStep > 1);
      this.setHidden(this.q('[data-review-title]'), this.currentStep < 9);
      // Only the current step is ever shown - every other step (already
      // completed or not yet reached) is fully hidden. What was chosen on
      // completed steps is recapped once, below the current step's own
      // fields, by [data-step-recap] instead of being repeated per step.
      this.qa('[data-step]').forEach(step => {
        this.setHidden(step, Number(step.dataset.step) !== this.currentStep);
      });

      // Shown as a native tooltip on a recap row flagged .cc6__recap-error
      // (native rather than a custom CSS popover since .cc6__step-recap
      // uses overflow:hidden for its rounded corners, which would clip an
      // absolutely-positioned tooltip that overflows the card).
      const recapErrorMessages = {
        1: 'Your delivery postcode is no longer eligible for the selected delivery method.',
        2: 'This canvas size is outside the limit allowed for the selected delivery method.',
        3: 'Please reselect a canvas surface.',
        4: 'Please reselect a Float Frame option.',
        5: 'Stringing/D-Ring choice needs to be reconfirmed for the current canvas size.',
        6: 'Art Box availability has changed for the current delivery or size selection.',
        7: 'Please reselect a production option.',
        8: 'Please enter a quantity of 1 or more.'
      };

      const recap = this.q('[data-step-recap]');
      if (recap) {
        // Also hidden while reviewingStep: the point of reopening a step
        // from Review Order is to focus on just that step's own fields,
        // not to keep showing the summary of everything else.
        this.setHidden(recap, this.currentStep <= 1 || reviewingStep);
        recap.querySelectorAll('[data-recap-step]').forEach(row => {
          const step = Number(row.dataset.recapStep);
          // Gated on unlockedStep rather than currentStep so that going
          // Back or using an Edit link to revisit an earlier step (neither
          // truncates progress) doesn't hide recap rows for steps already
          // passed.
          const rowHidden = step >= this.unlockedStep;
          this.setHidden(row, rowHidden);
          // Editing one step can invalidate a DIFFERENT, already-confirmed
          // step (e.g. changing Delivery can push a confirmed Size out of
          // range) - complete[] reflects every step's live validity every
          // update(), not just the one currently being edited, so this
          // catches that without needing to revisit the affected step.
          const hasError = !rowHidden && !complete[step];
          row.classList.toggle('cc6__recap-error', hasError);
          if (hasError) {
            row.title = recapErrorMessages[step] || 'This selection needs to be reconfirmed.';
          } else {
            row.removeAttribute('title');
          }
        });
      }

      // The complete desktop order summary remains available throughout all
      // nine guided steps; CSS owns its one stable sticky position.
      this.root.classList.remove('cc6--preview-sticky');
      this.updateLayoutOffsets();

      const nav = this.q('[data-step-nav]');
      const backButton = this.q('[data-wizard-back]');
      const nextButton = this.q('[data-wizard-next]');
      const activeStep = this.q(`[data-step="${this.currentStep}"]`);

      // Avoid moving nodes on every input event, only when the active step
      // actually changes - repeated DOM moves can trigger scroll anchoring
      // even when the active step hasn't changed.
      if (activeStep && recap && this.currentStep >= 9) {
        // STEP 9 keeps the recap as a sibling immediately before it,
        // above the Add to Cart/Cancel form, rather than nested inside
        // like the other steps.
        if (recap.nextElementSibling !== activeStep) {
          activeStep.parentElement.insertBefore(recap, activeStep);
        }
      }

      if (!nav || !backButton || !nextButton) return;
      if (activeStep && recap && this.currentStep < 9) {
        // STEPS 1-7: Back/Next then the recap below them. STEP 8
        // (Quantity) reverses that - Order Summary first, Back/Next below
        // it - since that's the step where the live total next to the
        // quantity control matters most.
        const [first, second] = this.currentStep === 8 ? [recap, nav] : [nav, recap];
        if (first.parentElement !== activeStep || first.nextElementSibling !== second) {
          activeStep.appendChild(first);
          activeStep.appendChild(second);
        }
      }

      // Step 9 (Review Order) carries its own Cancel/Add to Cart row in the
      // markup, so the shared Back/Next row is hidden outright there rather
      // than relocating Back into it.
      const onFinalStep = this.currentStep >= 9;
      this.setHidden(nav, onFinalStep);
      if (onFinalStep) return;

      // Once Review Order has been reached, editing an earlier step swaps
      // Back+Next for a single always-enabled "Return to Review Order"
      // button (see advanceWizard) - Back disappears since there's
      // nowhere sequential left to go back to, and validity no longer
      // gates the button since an error surfaces on the Review Order
      // screen instead of blocking navigation here.
      this.setHidden(backButton, this.currentStep <= 1 || reviewingStep);
      nextButton.textContent = reviewingStep
        ? 'Return to Review Order'
        : (this.currentStep === 8 ? 'Review Order' : 'Next');
      nextButton.disabled = reviewingStep ? false : !Array.from({ length: this.currentStep }, (_, index) => index + 1)
        .every(step => complete[step]);
      this.setHidden(nextButton, false);
    }

    dimensionLimits(delivery, unit) {
      if (unit === 'cm') {
        if (delivery === 'pickup') return { maxSide: 152, maxShort: 152 };
        if (delivery === 'local') return { maxSide: 152, maxShort: 122 };
        return { maxSide: 91, maxShort: 91 };
      }
      if (delivery === 'pickup') return { maxSide: 60, maxShort: 60 };
      if (delivery === 'local') return { maxSide: 60, maxShort: 48 };
      return { maxSide: 36, maxShort: 36 };
    }

    dimensionHelp({ delivery, min, unit }) {
      const limits = this.dimensionLimits(delivery, unit);
      const label = unit === 'cm' ? 'cm' : 'inches';
      if (delivery === 'local') {
        return `Minimum ${min} ${label}. Longest side up to ${limits.maxSide} ${label}; shortest side up to ${limits.maxShort} ${label}.`;
      }
      return `Enter values between ${min} and ${limits.maxSide} ${label}`;
    }

    validateDimensions({ width, height, min, unit, delivery }) {
      const limits = this.dimensionLimits(delivery, unit);
      const unitLabel = unit === 'cm' ? 'cm' : 'inches';
      const validate = (value, label) => {
        if (!Number.isFinite(value) || value <= 0) return `Enter a valid ${label.toLowerCase()}.`;
        if (value < min) return `${label} must be at least ${min} ${unitLabel}.`;
        if (value > limits.maxSide) return `${label} must be no more than ${limits.maxSide} ${unitLabel} for ${this.deliveryLabel(delivery)}.`;
        return '';
      };
      const errors = {
        width: validate(width, 'Width'),
        height: validate(height, 'Height')
      };
      if (delivery === 'local' && !errors.width && !errors.height) {
        const shortest = Math.min(width, height);
        if (shortest > limits.maxShort) {
          const message = `The shortest side must be no more than ${limits.maxShort} ${unitLabel} for Free Local Delivery.`;
          errors.width = message;
          errors.height = message;
        }
      }
      return errors;
    }

    renderDimensionValidation(errors) {
      [['width', errors.width], ['height', errors.height]].forEach(([field, message]) => {
        const input = this.q(`[data-${field}]`);
        const wrapper = this.q(`[data-${field}-wrap]`);
        const output = this.q(`[data-${field}-error]`);
        const invalid = Boolean(message);
        wrapper?.classList.toggle('is-invalid', invalid);
        input?.setAttribute('aria-invalid', String(invalid));
        if (output) {
          output.textContent = message;
          output.hidden = !invalid;
        }
      });
    }

    renderPreview(width, height, stringing, surface, dRings, showMeasurements = true) {
      const enteredWidth = Number.isFinite(width) && width > 0 ? width : 1;
      const enteredHeight = Number.isFinite(height) && height > 0 ? height : 1;
      // Keep the visual canvas upright: the shorter side is always horizontal.
      const safeWidth = Math.min(enteredWidth, enteredHeight);
      const safeHeight = Math.max(enteredWidth, enteredHeight);
      const previewUnit = this.checked('unit-') || 'in';
      const widthInches = previewUnit === 'cm' ? safeWidth / 2.54 : safeWidth;
      const heightInches = previewUnit === 'cm' ? safeHeight / 2.54 : safeHeight;
      const longestSideInches = Math.max(widthInches, heightInches);
      const shortestSideInches = Math.min(widthInches, heightInches);
      const showCentreBrace = longestSideInches >= 36;
      const showCornerBraces = longestSideInches >= 40 && shortestSideInches >= 30;
      const maxWidth = 255;
      const maxHeight = 175;
      const ratio = Math.max(0.05, Math.min(20, safeWidth / safeHeight));
      let canvasWidth = maxWidth;
      let canvasHeight = canvasWidth / ratio;
      if (canvasHeight > maxHeight) {
        canvasHeight = maxHeight;
        canvasWidth = canvasHeight * ratio;
      }
      canvasWidth = Math.max(24, canvasWidth);
      canvasHeight = Math.max(24, canvasHeight);
      const x = (385 - canvasWidth) / 2;
      const y = 42 + (190 - canvasHeight) / 2;
      const frame = Math.min(14, Math.max(8, Math.min(canvasWidth, canvasHeight) * .1));
      const depth = Math.min(11, Math.max(6, frame * .72));
      const wrap = Math.min(5, Math.max(3, frame * .34));

      this.setRect(this.q('[data-canvas-back]'), x, y, canvasWidth, canvasHeight);
      this.setRect(this.q('[data-face]'), x + frame, y + frame, Math.max(1, canvasWidth - frame * 2), Math.max(1, canvasHeight - frame * 2));

      const transparentLinen = surface === 'linen-clear';
      this.q('[data-canvas-back]').style.fill = transparentLinen ? '#d8c6a8' : '';
      const rearDepth = transparentLinen ? Math.max(3, depth * .45) : depth;

      this.q('[data-depth-side]').setAttribute('d', `M${x + canvasWidth},${y} l${rearDepth},${rearDepth} v${canvasHeight} l-${rearDepth},-${rearDepth} Z`);
      this.q('[data-depth-bottom]').setAttribute('d', `M${x},${y + canvasHeight} l${rearDepth},${rearDepth} h${canvasWidth} l-${rearDepth},-${rearDepth} Z`);
      this.q('[data-depth-side]').style.fill = transparentLinen ? '#d0bc9d' : '';
      this.q('[data-depth-bottom]').style.fill = transparentLinen ? '#c9b493' : '';

      this.q('[data-front-depth-side]').setAttribute('d', `M${x + canvasWidth},${y} l${depth},${depth} v${canvasHeight} l-${depth},-${depth} Z`);
      this.q('[data-front-depth-bottom]').setAttribute('d', `M${x},${y + canvasHeight} l${depth},${depth} h${canvasWidth} l-${depth},-${depth} Z`);
      this.q('[data-front-depth-side]').style.fill = transparentLinen ? '#c7b18d' : '';
      this.q('[data-front-depth-bottom]').style.fill = transparentLinen ? '#bca580' : '';
      this.setRect(this.q('[data-front-face]'), x, y, canvasWidth, canvasHeight);
      this.q('[data-front-face]').setAttribute(
        'fill',
        surface === 'linen-clear'
          ? `url(#canvas-weave-${this.root.dataset.sectionId})`
          : `url(#white-canvas-${this.root.dataset.sectionId})`
      );

      const frameLayer = this.q('[data-frame-layer]');
      frameLayer.innerHTML = `
        <path d="M${x + wrap},${y + wrap} H${x + canvasWidth - wrap} L${x + canvasWidth - frame},${y + frame} H${x + frame} Z"></path>
        <path d="M${x + wrap},${y + canvasHeight - wrap} H${x + canvasWidth - wrap} L${x + canvasWidth - frame},${y + canvasHeight - frame} H${x + frame} Z"></path>
        <path d="M${x + wrap},${y + wrap} V${y + canvasHeight - wrap} L${x + frame},${y + canvasHeight - frame} V${y + frame} Z"></path>
        <path d="M${x + canvasWidth - wrap},${y + wrap} V${y + canvasHeight - wrap} L${x + canvasWidth - frame},${y + canvasHeight - frame} V${y + frame} Z"></path>
      `;
      frameLayer.querySelectorAll('path').forEach(path => path.setAttribute('fill', `url(#timber-${this.root.dataset.sectionId})`));

      const braceLayer = this.q('[data-brace-layer]');
      const pixelsPerInch = Math.min(canvasWidth / Math.max(widthInches, .01), canvasHeight / Math.max(heightInches, .01));
      const centreBraceStroke = Math.min(20, Math.max(4, pixelsPerInch * 3));
      const cornerBraceStroke = Math.min(15, Math.max(3, pixelsPerInch * 2));
      const braceParts = [];
      const innerLeft = x + frame;
      const innerRight = x + canvasWidth - frame;
      const innerTop = y + frame;
      const innerBottom = y + canvasHeight - frame;
      const innerWidth = Math.max(1, innerRight - innerLeft);
      const innerHeight = Math.max(1, innerBottom - innerTop);
      const cornerReachX = Math.min(44, Math.max(6, innerWidth * .28), innerWidth / 2);
      const cornerReachY = Math.min(44, Math.max(6, innerHeight * .28), innerHeight / 2);

      // One centre brace only when at least one side is 36 inches or longer.
      if (showCentreBrace) {
        if (canvasWidth >= canvasHeight) {
          braceParts.push(`<line class="cc6__centre-brace" x1="${x + canvasWidth / 2}" y1="${innerTop}" x2="${x + canvasWidth / 2}" y2="${innerBottom}"></line>`);
        } else {
          braceParts.push(`<line class="cc6__centre-brace" x1="${innerLeft}" y1="${y + canvasHeight / 2}" x2="${innerRight}" y2="${y + canvasHeight / 2}"></line>`);
        }
      }

      // Four diagonal corner braces only when one side is at least 40 inches
      // and the other side is at least 30 inches.
      if (showCornerBraces) {
        braceParts.push(
          `<line class="cc6__corner-brace" x1="${innerLeft}" y1="${innerTop + cornerReachY}" x2="${innerLeft + cornerReachX}" y2="${innerTop}"></line>`,
          `<line class="cc6__corner-brace" x1="${innerRight - cornerReachX}" y1="${innerTop}" x2="${innerRight}" y2="${innerTop + cornerReachY}"></line>`,
          `<line class="cc6__corner-brace" x1="${innerLeft}" y1="${innerBottom - cornerReachY}" x2="${innerLeft + cornerReachX}" y2="${innerBottom}"></line>`,
          `<line class="cc6__corner-brace" x1="${innerRight - cornerReachX}" y1="${innerBottom}" x2="${innerRight}" y2="${innerBottom - cornerReachY}"></line>`
        );
      }
      braceLayer.innerHTML = braceParts.join('');
      braceLayer.querySelectorAll('line').forEach(line => {
        line.setAttribute('stroke', '#cfbda7');
        line.setAttribute(
          'stroke-width',
          line.classList.contains('cc6__centre-brace') ? centreBraceStroke : cornerBraceStroke
        );
        line.setAttribute('stroke-opacity', '.68');
        // Flat caps ensure the braces finish precisely at the inside bar edge.
        line.setAttribute('stroke-linecap', 'butt');
      });

      const stapleLayer = this.q('[data-staple-layer]');
      const staples = [];
      const spacing = 26;
      for (let sx = x + frame + 9; sx < x + canvasWidth - frame - 5; sx += spacing) {
        staples.push(`<line x1="${sx}" y1="${y + 4}" x2="${sx + 5}" y2="${y + 4}"></line>`);
        staples.push(`<line x1="${sx}" y1="${y + canvasHeight - 4}" x2="${sx + 5}" y2="${y + canvasHeight - 4}"></line>`);
      }
      for (let sy = y + frame + 9; sy < y + canvasHeight - frame - 5; sy += spacing) {
        staples.push(`<line x1="${x + 4}" y1="${sy}" x2="${x + 4}" y2="${sy + 5}"></line>`);
        staples.push(`<line x1="${x + canvasWidth - 4}" y1="${sy}" x2="${x + canvasWidth - 4}" y2="${sy + 5}"></line>`);
      }
      stapleLayer.innerHTML = staples.join('');

      const isWiderThanTall = safeWidth > safeHeight;
      const isSquare = Math.abs(safeWidth - safeHeight) < 0.001;

      // â€œLandscapeâ€ means string along the canvas's longest direction.
      // â€œPortraitâ€ means string along the canvas's shortest direction.
      // A square's single â€œYes Please Stringâ€ choice remains horizontal.
      let drawHorizontal = false;
      let drawVertical = false;

      if (stringing === 'yes') {
        drawHorizontal = true;
      } else if (stringing === 'landscape') {
        drawHorizontal = isWiderThanTall || isSquare;
        drawVertical = !isWiderThanTall && !isSquare;
      } else if (stringing === 'portrait') {
        drawVertical = isWiderThanTall || isSquare;
        drawHorizontal = !isWiderThanTall && !isSquare;
      } else if (stringing === 'both') {
        drawHorizontal = true;
        drawVertical = true;
      }

      const wireLayer = this.q('[data-wire-layer]');
      const wireParts = [];

      if (this.unlockedStep >= 5 && drawHorizontal) {
        // Attach to the left and right stretcher bars, one-third down from the top.
        const x1 = x + frame / 2;
        const x2 = x + canvasWidth - frame / 2;
        const wireY = y + canvasHeight / 3;
        const dipY = Math.min(
          y + canvasHeight - frame,
          wireY + Math.max(8, Math.min(24, canvasHeight * 0.14))
        );

        wireParts.push(
          `<path class="cc6__wire" style="stroke:url(#wire-braid-${this.root.dataset.sectionId})" d="M${x1 + 4},${wireY} Q${(x1 + x2) / 2},${dipY} ${x2 - 4},${wireY}"></path>`
        );
      }

      if (this.unlockedStep >= 5 && drawVertical) {
        // Vertical equivalent: attach to the top and bottom bars, one-third
        // across from the left edge.
        const wireX = x + canvasWidth / 3;
        const y1 = y + frame / 2;
        const y2 = y + canvasHeight - frame / 2;
        const dipX = Math.min(
          x + canvasWidth - frame,
          wireX + Math.max(8, Math.min(24, canvasWidth * 0.14))
        );

        wireParts.push(
          `<path class="cc6__wire" style="stroke:url(#wire-braid-${this.root.dataset.sectionId})" d="M${wireX},${y1 + 4} Q${dipX},${(y1 + y2) / 2} ${wireX},${y2 - 4}"></path>`
        );
      }

      wireLayer.innerHTML = wireParts.join('');

      const dRingLayer = this.q('[data-dring-layer]');
      const dRingParts = [];
      if (this.unlockedStep >= 5 && dRings) {
        const sixCmInches = 6 / 2.54;
        const offset = Math.max(3, sixCmInches * pixelsPerInch);
        const addLandscapePair = stringing === 'no' || stringing === 'yes' || drawHorizontal;
        const addPortraitPair = stringing === 'both' || (!drawHorizontal && drawVertical);

        if (addLandscapePair) {
          const ringY = Math.min(innerBottom - 2, innerTop + offset);
          dRingParts.push(
            this.ringMarkup(x + frame / 2, ringY, 'horizontal'),
            this.ringMarkup(x + canvasWidth - frame / 2, ringY, 'horizontal')
          );
        }
        if (addPortraitPair) {
          const ringX = Math.min(innerRight - 2, innerLeft + offset);
          dRingParts.push(
            this.ringMarkup(ringX, y + frame / 2, 'vertical'),
            this.ringMarkup(ringX, y + canvasHeight - frame / 2, 'vertical')
          );
        }
      }
      dRingLayer.innerHTML = dRingParts.join('');

      const measureLayer = this.q('[data-measure-layer]');
      const marker = `url(#arrow-${this.root.dataset.sectionId})`;
      const measurementUnit = (this.checked('unit-') || 'in') === 'in' ? 'inches' : 'cm';
      const widthText = `${this.trim(safeWidth)} ${measurementUnit}`;
      const heightText = `${this.trim(safeHeight)} ${measurementUnit}`;
      measureLayer.innerHTML = this.unlockedStep < 2 || !showMeasurements ? '' : `
        <line x1="${x}" y1="${y - 18}" x2="${x + canvasWidth}" y2="${y - 18}" marker-start="${marker}" marker-end="${marker}"></line>
        <text x="${x + canvasWidth / 2}" y="${y - 24}" text-anchor="middle">${widthText}</text>
        <line x1="${x - 18}" y1="${y}" x2="${x - 18}" y2="${y + canvasHeight}" marker-start="${marker}" marker-end="${marker}"></line>
        <text x="${x - 25}" y="${y + canvasHeight / 2}" text-anchor="middle" transform="rotate(-90 ${x - 25} ${y + canvasHeight / 2})">${heightText}</text>
      `;
      this.updatePreviewView();
    }

    updatePreviewView() {
      const showFront = this.previewView === 'front';
      this.setHidden(this.q('[data-front-object]'), !showFront);
      this.setHidden(this.q('[data-canvas-object]'), showFront);
      this.qa('[data-preview-view]').forEach(button => {
        const active = button.dataset.previewView === this.previewView;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      const svg = this.q('.cc6__preview svg');
      if (svg) svg.setAttribute('aria-label', `Proportionate ${showFront ? 'front' : 'rear'} view of the configured canvas`);
    }

    switchPreviewView(nextView) {
      if (nextView === this.previewView || this.previewAnimating) return;
      const svg = this.q('.cc6__preview svg');
      if (!svg || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.previewView = nextView;
        this.updatePreviewView();
        return;
      }

      this.previewAnimating = true;
      svg.classList.add('is-flipping');
      window.setTimeout(() => {
        this.previewView = nextView;
        this.updatePreviewView();
        svg.classList.remove('is-flipping');
        window.setTimeout(() => { this.previewAnimating = false; }, 350);
      }, 350);
    }

    ringMarkup(cx, cy, orientation) {
      const rotate = orientation === 'vertical' ? ` transform="rotate(90 ${cx} ${cy})"` : '';
      return `<g class="cc6__d-ring"${rotate}><circle cx="${cx}" cy="${cy}" r="2"></circle><circle class="cc6__screw" cx="${cx}" cy="${cy}" r=".5"></circle><path d="M${cx - 1.7},${cy} Q${cx},${cy + 2.8} ${cx + 1.7},${cy}"></path></g>`;
    }

    populateForm({ quantity, size, deliveryText, postcode, surfaceText, stringingText, dRingsText, productionText, frameText, artboxText, variant }) {
      const values = {
        '[data-form-qty]': quantity,
        '[data-prop-size]': size,
        '[data-prop-delivery]': deliveryText,
        '[data-prop-postcode]': postcode || '',
        '[data-prop-surface]': surfaceText,
        '[data-prop-string]': stringingText,
        '[data-prop-drings]': dRingsText,
        '[data-prop-production]': productionText,
        '[data-prop-frame]': frameText,
        '[data-prop-artbox]': artboxText,
        '[data-prop-notes]': this.q('[data-notes]')?.value || '',
        '[data-variant-id]': variant?.id || ''
      };
      Object.entries(values).forEach(([selector, value]) => { this.q(selector).value = value; });
    }

    renderDebug({ unitPrice, variant, error }) {
      const panel = this.q('[data-debug-panel]');
      const enabled = this.root.dataset.debug === 'true';
      this.setHidden(panel, !enabled);
      if (!enabled) return;
      this.q('[data-debug-calculated]').textContent = this.money(unitPrice);
      this.q('[data-debug-option]').textContent = variant ? String(unitPrice) : 'No match';
      this.q('[data-debug-price]').textContent = variant ? this.money(variant.price) : '—';
      this.q('[data-debug-status]').textContent = error || 'Verified';
    }

    setHidden(element, hidden) {
      if (!element) return;
      if (hidden) {
        element.setAttribute('hidden', '');
      } else {
        element.removeAttribute('hidden');
      }
      element.style.display = hidden ? 'none' : '';
    }

    setRect(element, x, y, width, height) {
      element.setAttribute('x', x);
      element.setAttribute('y', y);
      element.setAttribute('width', width);
      element.setAttribute('height', height);
    }

    setCircle(element, cx, cy) {
      element.setAttribute('cx', cx);
      element.setAttribute('cy', cy);
    }

    money(value) {
      return `${this.root.dataset.currency || '$'}${Number(value || 0).toLocaleString('en-AU', { minimumFractionDigits: Number(value) % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
    }

    formatCentimetres(value) {
      return String(Number((Math.round(Number(value) * 10) / 10).toFixed(1)));
    }

    format(value, decimals) {
      return Number(value || 0).toLocaleString('en-AU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    }

    trim(value) {
      return Number(Number(value || 0).toFixed(2)).toString();
    }

  }

  function initialise(scope = document) {
    scope.querySelectorAll('[data-cc6]').forEach(root => {
      if (!root.dataset.ready) {
        root.dataset.ready = 'true';
        new CanvasCalculator(root);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => initialise());
  document.addEventListener('shopify:section:load', event => initialise(event.target));
})();

/* ArtSmart V6 sticky summary fix — confirmed 2026-08-19 */
